import { toast } from "@ui/molecules/Toasts/toast";
import { getPreferredLocale } from "@utils/locale";
import { createSignal, onCleanup } from "solid-js";

type SpeechRecognitionResultList = {
	length: number;
	[index: number]: {
		isFinal: boolean;
		[index: number]: { transcript: string };
	};
};

type SpeechRecognitionEvent = Event & {
	resultIndex: number;
	results: SpeechRecognitionResultList;
};

type SpeechRecognitionErrorEvent = Event & {
	error: string;
	message?: string;
};

type BrowserSpeechRecognition = EventTarget & {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	start: () => void;
	stop: () => void;
	abort: () => void;
	onresult: ((event: SpeechRecognitionEvent) => void) | null;
	onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
	onend: (() => void) | null;
};

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
	if (typeof window === "undefined") {
		return null;
	}

	const browserWindow = window as Window & {
		SpeechRecognition?: SpeechRecognitionConstructor;
		webkitSpeechRecognition?: SpeechRecognitionConstructor;
	};

	return (
		browserWindow.SpeechRecognition ??
		browserWindow.webkitSpeechRecognition ??
		null
	);
}

export function isSpeechToTextSupported(): boolean {
	return getSpeechRecognitionConstructor() !== null;
}

type UseSpeechToTextOptions = {
	disabled?: () => boolean;
	onBeginSession?: () => void;
	onEndSession?: () => void;
	onTranscript: (text: string, isFinal: boolean) => void;
};

export function useSpeechToText(options: UseSpeechToTextOptions) {
	const [isListening, setIsListening] = createSignal(false);
	let recognition: BrowserSpeechRecognition | null = null;
	let shouldRestart = false;

	const stop = () => {
		shouldRestart = false;
		setIsListening(false);
		recognition?.stop();
	};

	const start = () => {
		if (options.disabled?.()) {
			return;
		}

		const Recognition = getSpeechRecognitionConstructor();
		if (!Recognition) {
			toast.error({
				title: "Voice input unavailable",
				description: "Your browser does not support speech recognition.",
			});
			return;
		}

		if (!recognition) {
			recognition = new Recognition();
			recognition.continuous = true;
			recognition.interimResults = true;
			recognition.lang = getPreferredLocale() ?? "en-US";

			recognition.onresult = (event) => {
				let interimTranscript = "";
				let finalTranscript = "";

				for (let i = event.resultIndex; i < event.results.length; i++) {
					const result = event.results[i];
					const transcript = result[0]?.transcript ?? "";
					if (result.isFinal) {
						finalTranscript += transcript;
					} else {
						interimTranscript += transcript;
					}
				}

				if (finalTranscript.trim().length > 0) {
					options.onTranscript(finalTranscript, true);
				}
				if (interimTranscript.length > 0) {
					options.onTranscript(interimTranscript, false);
				}
			};

			recognition.onerror = (event) => {
				if (event.error === "aborted" || event.error === "no-speech") {
					return;
				}

				shouldRestart = false;
				setIsListening(false);

				if (event.error === "not-allowed") {
					toast.error({
						title: "Microphone access denied",
						description: "Allow microphone access to use voice input.",
					});
					return;
				}

				toast.error({
					title: "Voice input failed",
					description: event.message ?? "Speech recognition stopped unexpectedly.",
				});
			};

			recognition.onend = () => {
				if (!shouldRestart) {
					setIsListening(false);
					options.onEndSession?.();
					return;
				}

				try {
					recognition?.start();
				} catch {
					shouldRestart = false;
					setIsListening(false);
					options.onEndSession?.();
				}
			};
		}

		try {
			options.onBeginSession?.();
			shouldRestart = true;
			setIsListening(true);
			recognition.start();
		} catch {
			shouldRestart = false;
			setIsListening(false);
			toast.error({
				title: "Voice input unavailable",
				description: "Could not start speech recognition.",
			});
		}
	};

	const toggle = () => {
		if (isListening()) {
			stop();
			options.onEndSession?.();
			return;
		}
		start();
	};

	onCleanup(() => {
		shouldRestart = false;
		recognition?.abort();
		recognition = null;
	});

	return {
		isSupported: isSpeechToTextSupported,
		isListening,
		toggle,
		stop,
	};
}
